import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, QrCode, Smartphone, Store, Users, Apple, Play, Monitor, Tablet, Star, Zap, CheckCircle } from 'lucide-react';

interface AppDownloadModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const AppDownloadModal: React.FC<AppDownloadModalProps> = ({ isOpen, onClose }) => {
  const [step, setStep] = useState<'selection' | 'qr'>('selection');
  const [selectedType, setSelectedType] = useState<'customer' | 'vendor' | null>(null);
  const [userOS, setUserOS] = useState('');
  const [deviceType, setDeviceType] = useState('');

  useEffect(() => {
    if (isOpen) {
      detectDevice();
    }
  }, [isOpen]);

  useEffect(() => {
    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscapeKey);
    }

    return () => {
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [isOpen, onClose]);

  const detectDevice = () => {
    const userAgent = navigator.userAgent || navigator.vendor;
    
    // Detect OS
    if (/android/i.test(userAgent)) {
      setUserOS('Android');
    } else if (/iPad|iPhone|iPod/.test(userAgent)) {
      setUserOS('iOS');
    } else if (/Windows/i.test(userAgent)) {
      setUserOS('Windows');
    } else if (/Mac/i.test(userAgent)) {
      setUserOS('macOS');
    } else {
      setUserOS('Unknown');
    }

    // Detect device type
    if (/iPad|iPhone|iPod|Android/i.test(userAgent)) {
      setDeviceType('mobile');
    } else if (/iPad/i.test(userAgent)) {
      setDeviceType('tablet');
    } else {
      setDeviceType('desktop');
    }
  };

  const getAppStoreURL = (type: 'customer' | 'vendor', os: string) => {
    const baseUrls = {
      customer: {
        iOS: 'https://apps.apple.com/app/dailzy-customer',
        Android: 'https://play.google.com/store/apps/details?id=com.dailzy.customer'
      },
      vendor: {
        iOS: 'https://apps.apple.com/app/dailzy-vendor',
        Android: 'https://play.google.com/store/apps/details?id=com.dailzy.vendor'
      }
    };
    
    return baseUrls[type][os as keyof typeof baseUrls.customer] || baseUrls[type]['Android'];
  };

  const getSmartQRCodeURL = (type: 'customer' | 'vendor') => {
    return `https://dailzy.com/${type}-smart-redirect`;
  };

  const getOSIcon = () => {
    switch (userOS) {
      case 'iOS': return <Apple className="w-4 h-4" />;
      case 'Android': return <Play className="w-4 h-4" />;
      case 'Windows': return <Monitor className="w-4 h-4" />;
      case 'macOS': return <Monitor className="w-4 h-4" />;
      default: return <Smartphone className="w-4 h-4" />;
    }
  };

  const getDeviceIcon = () => {
    switch (deviceType) {
      case 'mobile': return <Smartphone className="w-4 h-4" />;
      case 'tablet': return <Tablet className="w-4 h-4" />;
      default: return <Monitor className="w-4 h-4" />;
    }
  };

  const handleDirectDownload = () => {
    if (selectedType && deviceType === 'mobile') {
      const url = getAppStoreURL(selectedType, userOS);
      window.open(url, '_blank');
      onClose();
    }
  };

  const handleTypeSelection = (type: 'customer' | 'vendor') => {
    setSelectedType(type);
    if (deviceType === 'mobile') {
      // Direct download for mobile users
      const url = getAppStoreURL(type, userOS);
      window.open(url, '_blank');
      onClose();
    } else {
      // Show QR code for desktop/tablet users
      setStep('qr');
    }
  };

  const resetModal = () => {
    setStep('selection');
    setSelectedType(null);
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4"
        style={{ zIndex: 9999 }}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
      >
        <motion.div
          className="bg-white rounded-3xl p-8 max-w-md w-full max-h-[90vh] overflow-y-auto relative shadow-2xl"
          initial={{ scale: 0.8, opacity: 0, y: 50 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.8, opacity: 0, y: 50 }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Close Button */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X className="w-5 h-5" />
          </button>

          {step === 'selection' && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center"
            >
              <div className="mb-6">
                <div className="w-16 h-16 bg-gradient-to-r from-red-500 to-orange-500 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <Smartphone className="w-8 h-8 text-white" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">Download Dailzy App</h2>
                <p className="text-gray-600">Choose your app type to get started</p>
              </div>

              {/* Device Detection Info */}
              <div className="flex items-center justify-center gap-3 p-3 bg-gray-50 rounded-xl mb-6">
                <div className="flex items-center gap-2 text-gray-600">
                  {getOSIcon()}
                  <span className="text-sm">Detected: {userOS}</span>
                </div>
                <div className="w-1 h-4 bg-gray-300 rounded-full"></div>
                <div className="flex items-center gap-2 text-gray-600">
                  {getDeviceIcon()}
                  <span className="text-sm capitalize">{deviceType}</span>
                </div>
              </div>

              {/* App Type Selection */}
              <div className="space-y-4">
                <motion.button
                  onClick={() => handleTypeSelection('customer')}
                  className="w-full p-6 border-2 border-gray-200 rounded-2xl hover:border-red-500 hover:bg-red-50 transition-all duration-300 group"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 bg-gradient-to-r from-red-500 to-orange-500 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform">
                      <Users className="w-7 h-7 text-white" />
                    </div>
                    <div className="text-left">
                      <h3 className="text-lg font-bold text-gray-900 group-hover:text-red-600">Customer App</h3>
                      <p className="text-sm text-gray-600">Shop from local vendors with 10-min delivery</p>
                    </div>
                  </div>
                </motion.button>

                <motion.button
                  onClick={() => handleTypeSelection('vendor')}
                  className="w-full p-6 border-2 border-gray-200 rounded-2xl hover:border-red-500 hover:bg-red-50 transition-all duration-300 group"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 bg-gradient-to-r from-red-500 to-orange-500 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform">
                      <Store className="w-7 h-7 text-white" />
                    </div>
                    <div className="text-left">
                      <h3 className="text-lg font-bold text-gray-900 group-hover:text-red-600">Vendor App</h3>
                      <p className="text-sm text-gray-600">Manage your store and grow your business</p>
                    </div>
                  </div>
                </motion.button>
              </div>

              {deviceType === 'mobile' && (
                <p className="text-xs text-gray-500 mt-4">
                  You'll be redirected to the {userOS} app store
                </p>
              )}
            </motion.div>
          )}

          {step === 'qr' && selectedType && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center"
            >
              <div className="mb-6">
                <button
                  onClick={resetModal}
                  className="text-sm text-gray-500 hover:text-gray-700 mb-4 flex items-center gap-2"
                >
                  ← Back to selection
                </button>
                <div className="w-16 h-16 bg-gradient-to-r from-red-500 to-orange-500 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  {selectedType === 'customer' ? <Users className="w-8 h-8 text-white" /> : <Store className="w-8 h-8 text-white" />}
                </div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">
                  {selectedType === 'customer' ? 'Customer App' : 'Vendor App'}
                </h2>
                <p className="text-gray-600">Scan with your phone to download</p>
              </div>

              {/* QR Code */}
              <div className="bg-white border-4 border-gray-100 rounded-2xl p-6 mb-6">
                <img 
                  src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(getSmartQRCodeURL(selectedType))}`}
                  alt={`Download ${selectedType} app QR code`}
                  className="w-48 h-48 mx-auto"
                />
              </div>

              {/* Smart Detection Info */}
              {/* <div className="bg-gradient-to-r from-red-50 to-orange-50 rounded-xl p-4 mb-6">
                <div className="flex items-center justify-center gap-2 mb-2">
                  <Zap className="w-5 h-5 text-red-500" />
                  <span className="font-semibold text-gray-900">Smart QR Code</span>
                </div>
                <p className="text-sm text-gray-600">
                  This QR code automatically detects your phone's OS and redirects to the correct app store
                </p>
              </div> */}

              {/* Manual Download Links */}
              {/* <div className="space-y-3">
                <p className="text-sm font-medium text-gray-700">Or download directly:</p>
                <div className="flex gap-3">
                  <a
                    href={getAppStoreURL(selectedType, 'iOS')}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 flex items-center justify-center gap-2 bg-black text-white px-4 py-3 rounded-xl hover:bg-gray-800 transition-colors"
                  >
                    <Apple className="w-5 h-5" />
                    <span className="text-sm font-medium">iOS</span>
                  </a>
                  <a
                    href={getAppStoreURL(selectedType, 'Android')}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 flex items-center justify-center gap-2 bg-green-600 text-white px-4 py-3 rounded-xl hover:bg-green-700 transition-colors"
                  >
                    <Play className="w-5 h-5" />
                    <span className="text-sm font-medium">Android</span>
                  </a>
                </div>
              </div> */}

              {/* App Features */}
              <div className="mt-6 p-4 bg-gray-50 rounded-xl">
                <h4 className="font-semibold text-gray-900 mb-3">
                  {selectedType === 'customer' ? 'Customer Features' : 'Vendor Features'}
                </h4>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  {selectedType === 'customer' ? (
                    <>
                      <div className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-green-500" />
                        <span>10-min delivery</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-green-500" />
                        <span>Track orders</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-green-500" />
                        <span>Local vendors</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-green-500" />
                        <span>Best prices</span>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-green-500" />
                        <span>Manage orders</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-green-500" />
                        <span>Analytics</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-green-500" />
                        <span>Inventory</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-green-500" />
                        <span>Payouts</span>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </motion.div>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default AppDownloadModal; 